<script setup lang="ts">
import { computed } from 'vue'
import { useExtendedI18n } from '@/i18n'
import { useAuthStore } from '@/modules/auth/stores/auth-store'
import { useAddressStore } from '@/modules/shared/stores/address-store'
import { validMoney } from '@/modules/shared/utils/money'
import { VAlert, VButton, VIcon, VSection } from '@/modules/shared/components'
import type { ExistingFile } from '@/modules/shared/components/the-uploader.vue'
import type { AddOn, Subscription } from '../../../types'
import type { OnboardingStep } from '@/modules/shared/composables/use-onboarding'
import OnboardingReviewItem from '@/modules/shared/components/onboarding/OnboardingReviewItem.vue'
import OnboardingReviewSection from '@/modules/shared/components/onboarding/OnboardingReviewSection.vue'
import OnboardingPlanSummary from '@/modules/shared/components/onboarding/OnboardingPlanSummary.vue'

const props = withDefaults(
  defineProps<{
    form: any
    v$: any
    steps: OnboardingStep[]
    subscriptionOptions: Subscription[]
    addOnOptions: AddOn[]
    selectedPlan?: Subscription | null
    ownershipTypeOptions: { label: string; value: string }[]
    managerOptions: { label: string; value: string }[]
    adminOptions: { label: string; value: string }[]
    isRIA: boolean
    isRecommended: (subscription: Subscription) => boolean
    totalDue: string
    files: File[]
    existingFiles: ExistingFile[]
    isEditMode?: boolean
  }>(),
  {
    isEditMode: false,
  },
)

const emit = defineEmits<{
  (e: 'navigate', stepIndex: number): void
  (e: 'delete'): void
}>()

const { n } = useExtendedI18n()
const authStore = useAuthStore()
const addressStore = useAddressStore()

const stepIndex = (key: string) => props.steps.findIndex((s) => s.key === key)

// Friendly field labels for the validation summary, so a generic message like
// "Value is required" reads as "Document signer: Value is required".
// (Interim map — the stashed validation registry exposes SPV_FIELD_LABELS, which
// should replace this during the refactor.)
const FIELD_LABELS: Record<string, string> = {
  is_venture360_the_administrator: 'V360 administrator',
  is_venture360_providing_formation_documents: 'Formation documents',
  expect_any_activity: 'Post-year activity',
  kind_of_activity: 'Activity types',
  raising_status: 'Raising status',
  investor_range: 'Expected investors',
  is_raising_under_250k: 'Raise size',
  selected_services: 'Plan',
  name: 'Display name',
  legal_name: 'Legal name',
  date: 'Date established',
  tax_id: 'EIN',
  management_fee_percentage: 'Management fee',
  carried_interest: 'Carried interest',
  preferred_return: 'Preferred return',
  manager_id: 'SPV manager',
  admin_signer_id: 'Document signer',
  admin_ids: 'Administrators',
  company_name: 'Company name',
  is_requesting_bank_connection: 'Banking partner',
  disbursement_wire_repeat_bank_account_number: 'Confirm bank account number',
  is_filing_required: 'Tax return required',
  not_filing_reason: 'Not filing reason',
}

// Outstanding validation errors as "<Field>: <message>" lines, shown at the top
// of the review step. First pass — surfaces Vuelidate messages with the field
// name prepended; a per-field friendly version can come later.
const errorLines = computed<string[]>(() => {
  const errors = props.v$?.$errors ?? []
  const lines = errors.map((e: any) => `${FIELD_LABELS[e.$property] ?? e.$property}: ${e.$message}`)
  return [...new Set(lines)]
})

// ── Subscription summary ────────────────────────────────────────

const selectedSubscription = computed(() => props.selectedPlan || null)

const selectedAddOns = computed(() => props.addOnOptions.filter((a) => props.form.selected_services.includes(a.value)))

const hasSubscription = computed(
  () => props.form.is_venture360_the_administrator && props.form.selected_services.length > 0,
)

const isSelectedPlanRecommended = computed(
  () => selectedSubscription.value && props.isRecommended(selectedSubscription.value),
)

// ── Helpers ─────────────────────────────────────────────────────

const val = (v: any) => (v === null || v === undefined || v === '' ? '—' : String(v))

const formatCurrency = (money: any) => {
  if (!validMoney(money)) return '—'
  return n(money, 'currency')
}

const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

const formatPercent = (v: any) => {
  if (v === null || v === undefined || v === '') return '—'
  return `${v}%`
}

const lookupLabel = (options: { label: string; value: string }[], value: string) => {
  if (!value) return '—'
  return options.find((o) => o.value === value)?.label ?? val(value)
}

const adminNames = computed(() => {
  if (!props.form.admin_ids?.length) return '—'
  return props.form.admin_ids
    .map((id: string) => props.adminOptions.find((o) => o.value === id)?.label ?? id)
    .join(', ')
})

const stateName = (code: string) => {
  if (!code) return ''
  return addressStore.stateOptions.find((o) => o.value === code)?.label ?? code
}

const countryName = (code: string) => {
  if (!code) return ''
  return addressStore.countryOptions.find((o) => o.value === code)?.label ?? code
}

const addressLine = computed(() => {
  const parts = [
    props.form.street1,
    props.form.street2,
    props.form.city,
    stateName(props.form.state),
    props.form.zip,
    countryName(props.form.country),
  ].filter(Boolean)
  return parts.length ? parts.join(', ') : '—'
})

// ── Review rows ─────────────────────────────────────────────────

interface ReviewRow {
  label: string
  value: string
  stepIndex: number
}

interface ReviewSection {
  title: string
  rows: ReviewRow[]
}

const sections = computed<ReviewSection[]>(() => {
  const result: ReviewSection[] = []
  const si = stepIndex

  // SPV configuration
  const configRows: ReviewRow[] = [
    {
      label: 'V360 administrator',
      value: props.form.is_venture360_the_administrator ? 'Yes' : 'No',
      stepIndex: si('config'),
    },
  ]
  if (props.form.is_venture360_the_administrator) {
    configRows.push({
      label: 'Formation documents',
      value:
        props.form.is_venture360_providing_formation_documents === true
          ? 'Venture360'
          : props.form.is_venture360_providing_formation_documents === false
            ? 'Proprietary'
            : '—',
      stepIndex: si('config'),
    })
    configRows.push({
      label: 'Post-year activity',
      value: props.form.expect_any_activity === true ? 'Yes' : props.form.expect_any_activity === false ? 'No' : '—',
      stepIndex: si('config'),
    })
    if (props.form.expect_any_activity && props.form.kind_of_activity?.length) {
      configRows.push({
        label: 'Activity types',
        value: props.form.kind_of_activity.join(', '),
        stepIndex: si('config'),
      })
    }
  }
  configRows.push({
    label: 'Raising status',
    value:
      props.form.raising_status === 'currently_raising'
        ? 'Currently raising'
        : props.form.raising_status === 'closed'
          ? 'Closed / Not raising'
          : '—',
    stepIndex: si('config'),
  })
  // Expected investors and raise size only drive V360 pricing, so they appear
  // only when Venture360 is the administrator (mirrors the config step).
  if (props.form.is_venture360_the_administrator) {
    configRows.push({
      label: 'Expected investors',
      value: val(props.form.investor_range),
      stepIndex: si('config'),
    })
    configRows.push({
      label: 'Raising under $250k',
      value:
        props.form.is_raising_under_250k === true ? 'Yes' : props.form.is_raising_under_250k === false ? 'No' : '—',
      stepIndex: si('config'),
    })
  }
  result.push({ title: 'SPV configuration', rows: configRows })

  // General structure
  result.push({
    title: 'General structure',
    rows: [
      { label: 'Legal name', value: val(props.form.legal_name), stepIndex: si('structure') },
      { label: 'Display name', value: val(props.form.name), stepIndex: si('structure') },
      { label: 'Legal structure', value: val(props.form.type), stepIndex: si('structure') },
      {
        label: 'State of incorporation',
        value: props.form.state_of_incorporation ? stateName(props.form.state_of_incorporation) : '—',
        stepIndex: si('structure'),
      },
      { label: 'EIN', value: val(props.form.tax_id), stepIndex: si('structure') },
      { label: 'Date established', value: val(props.form.date), stepIndex: si('structure') },
      {
        label: 'Ownership allocation',
        value: lookupLabel(props.ownershipTypeOptions, props.form.ownership_type),
        stepIndex: si('structure'),
      },
      { label: 'Currency', value: val(props.form.currency), stepIndex: si('structure') },
      {
        label: 'Management fee',
        value: formatPercent(props.form.management_fee_percentage),
        stepIndex: si('structure'),
      },
      { label: 'Carried interest', value: formatPercent(props.form.carried_interest), stepIndex: si('structure') },
      { label: 'Preferred return', value: formatPercent(props.form.preferred_return), stepIndex: si('structure') },
    ],
  })

  // Administration
  result.push({
    title: 'Administration',
    rows: [
      { label: 'SPV manager', value: lookupLabel(props.managerOptions, props.form.manager_id), stepIndex: si('admin') },
      {
        label: 'Document signer',
        value: lookupLabel(props.adminOptions, props.form.admin_signer_id),
        stepIndex: si('admin'),
      },
      { label: 'Administrators', value: adminNames.value, stepIndex: si('admin') },
      { label: 'Entity address', value: addressLine.value, stepIndex: si('admin') },
    ],
  })

  // Investment details
  const descriptionDisplay = computed(() => {
    const desc = props.form.company_description
    if (!desc) return '—'
    return desc.length > 60 ? desc.substring(0, 60) + '...' : desc
  })

  const investmentRows: ReviewRow[] = [
    { label: 'Company name', value: val(props.form.company_name), stepIndex: si('investment') },
    { label: 'Description', value: descriptionDisplay.value, stepIndex: si('investment') },
    { label: 'Website', value: val(props.form.company_website), stepIndex: si('investment') },
    { label: 'Sector', value: val(props.form.sector), stepIndex: si('investment') },
    { label: 'Type of investment', value: val(props.form.type_of_investment), stepIndex: si('investment') },
  ]
  if (props.form.type_of_investment === 'Other') {
    investmentRows.push({
      label: 'Other type',
      value: val(props.form.other_investment_type),
      stepIndex: si('investment'),
    })
  }
  investmentRows.push(
    { label: 'Total being raised', value: formatCurrency(props.form.raised_amount), stepIndex: si('investment') },
    {
      label: 'Minimum investment',
      value: formatCurrency(props.form.minimum_investment_amount),
      stepIndex: si('investment'),
    },
    { label: 'Expected closing date', value: val(props.form.expected_close_date), stepIndex: si('investment') },
  )
  result.push({ title: 'Investment details', rows: investmentRows })

  // Bank details
  const bankRows: ReviewRow[] = []
  if (props.form.is_venture360_the_administrator) {
    bankRows.push({
      label: 'Banking partner',
      value: props.form.is_requesting_bank_connection ? 'V360 will connect' : 'No',
      stepIndex: si('bank'),
    })
  }
  bankRows.push(
    { label: 'Bank name', value: val(props.form.disbursement_wire_bank_name), stepIndex: si('bank') },
    { label: 'Bank address', value: val(props.form.disbursement_wire_bank_address), stepIndex: si('bank') },
    { label: 'Routing number', value: val(props.form.disbursement_wire_bank_routing_number), stepIndex: si('bank') },
    { label: 'SWIFT code', value: val(props.form.disbursement_wire_bank_swift_code), stepIndex: si('bank') },
    {
      label: 'Account number',
      value: props.form.disbursement_wire_bank_account_number ? '****' : '—',
      stepIndex: si('bank'),
    },
    { label: 'Account name', value: val(props.form.disbursement_wire_account_name), stepIndex: si('bank') },
    {
      label: 'For further credit to',
      value: val(props.form.disbursement_wire_for_further_credit_to),
      stepIndex: si('bank'),
    },
    {
      label: 'Wiring instructions',
      value: props.form.capital_call_wiring_instructions ? 'Provided' : '—',
      stepIndex: si('bank'),
    },
  )
  result.push({ title: 'Bank details', rows: bankRows })

  // Tax details (site admin only)
  if (authStore.is_site_admin) {
    const taxRows: ReviewRow[] = [
      {
        label: 'Tax return required',
        value: props.form.is_filing_required ? 'Yes' : 'No',
        stepIndex: si('tax'),
      },
    ]
    if (!props.form.is_filing_required) {
      taxRows.push({ label: 'Not filing reason', value: val(props.form.not_filing_reason), stepIndex: si('tax') })
    }
    if (props.form.is_filing_required) {
      taxRows.push(
        { label: 'Starting tax year', value: val(props.form.starting_tax_year), stepIndex: si('tax') },
        { label: 'Ending tax year', value: val(props.form.ending_tax_year), stepIndex: si('tax') },
        { label: 'Tax locator', value: val(props.form.gosystem_locator), stepIndex: si('tax') },
        { label: 'Tax basis', value: val(props.form.basis), stepIndex: si('tax') },
        { label: 'State filing', value: val(props.form.state_filing), stepIndex: si('tax') },
      )
    }
    result.push({ title: 'Tax details', rows: taxRows })
  }

  return result
})
</script>

<template>
  <div>
    <!-- Validation summary -->
    <VAlert v-if="errorLines.length" type="error" class="mb-4">
      <p class="font-medium">Please fix the following before saving:</p>
      <ul class="mt-1 list-disc space-y-0.5 pl-5">
        <li v-for="line in errorLines" :key="line">{{ line }}</li>
      </ul>
    </VAlert>

    <!-- Subscription summary card -->
    <OnboardingPlanSummary
      v-if="hasSubscription && selectedSubscription"
      :title="selectedSubscription.title"
      :price="selectedSubscription.price"
      :features="selectedSubscription.included"
      :add-ons="selectedAddOns"
      :total-due="totalDue"
      :is-recommended="isSelectedPlanRecommended"
      @click="emit('navigate', stepIndex('config'))"
    />

    <!-- Review sections -->
    <OnboardingReviewSection v-for="section in sections" :key="section.title" :title="section.title">
      <OnboardingReviewItem
        v-for="row in section.rows"
        :key="row.label"
        :label="row.label"
        :value="row.value"
        @click="emit('navigate', row.stepIndex)"
      />

      <!-- Supporting documents under Investment details -->
      <OnboardingReviewItem
        v-if="section.title === 'Investment details'"
        label="Supporting documents"
        :value="!existingFiles.length && !files.length ? 'None' : undefined"
        :layout="existingFiles.length || files.length ? 'stack' : 'row'"
        @click="emit('navigate', stepIndex('investment'))"
      >
        <template v-if="existingFiles.length || files.length">
          <div class="flex flex-col gap-1.5">
            <div v-for="file in existingFiles" :key="file.id" class="flex items-center gap-2">
              <VIcon name="FileText" class="h-3.5 w-3.5 shrink-0 text-[#009192]" />
              <span class="flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-[13px] text-gray-600">{{
                file.name
              }}</span>
            </div>
            <div v-for="(file, idx) in files" :key="`new-${idx}`" class="flex items-center gap-2">
              <VIcon name="FileText" class="h-3.5 w-3.5 shrink-0 text-[#009192]" />
              <span class="flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-[13px] text-gray-600">{{
                file.name
              }}</span>
              <span class="shrink-0 text-[11px] text-[#9ca3af]">{{ formatFileSize(file.size) }}</span>
            </div>
          </div>
        </template>
        <template v-else-if="existingFiles?.length">
          <div class="flex flex-col gap-1.5">
            <div v-for="(name, idx) in existingFiles" :key="idx" class="flex items-center gap-2">
              <VIcon name="FileText" class="h-3.5 w-3.5 shrink-0 text-[#009192]" />
              <span class="flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-[13px] text-gray-600">{{
                name
              }}</span>
            </div>
          </div>
        </template>
      </OnboardingReviewItem>
    </OnboardingReviewSection>

    <!-- Danger Zone -->
    <VSection v-if="isEditMode" label="Danger Zone" id="danger_zone" class="mt-24 scroll-mt-8">
      <div class="space-y-4">
        <VAlert type="error">
          Deleting this SPV will permanently remove all associated data including commitments, capital calls,
          distributions, and documents. This action cannot be undone.
        </VAlert>
        <VButton variant="v-red" :click="() => emit('delete')">Delete SPV</VButton>
      </div>
    </VSection>
  </div>
</template>
